/**
 * Transport for the two first-party endpoints this app owns on the Tabbio
 * Developer Platform: the "Sign in with Tabbio" consent screen
 * (`/api/oauth/authorize/*`) and the user's connected apps
 * (`/api/connected-apps`). Registering an app lives in the developer platform
 * app (platform.tabbio.com), not here.
 *
 * The app envelope is unwrapped here, exactly once, so screens and hooks only
 * ever see typed data or a thrown error (apps/mobile/AGENTS.md, "Frontend
 * Response Handling").
 */
import type {
  APIResponse,
  ConnectedAppSummary,
  OAuthApproveInput,
  OAuthAuthorizeRequestParams,
  OAuthConsentContext,
  OAuthRedirectResult,
  UpdateConnectedAppInput,
} from '@tabbio/types';
import { api } from './api';

/**
 * A consent request that cannot be shown as a consent screen.
 *
 * `errorRedirect` is set for the failures RFC 6749 says to hand back to the
 * client (bad scope, missing PKCE); it is null for the ones that must never
 * redirect (unknown client, redirect URI mismatch), which is what stops this
 * screen being an open redirect.
 */
export class OAuthConsentError extends Error {
  readonly code: string;
  readonly errorRedirect: string | null;

  constructor(
    message: string,
    options: { code: string; errorRedirect?: string | null },
  ) {
    super(message);
    this.name = 'OAuthConsentError';
    this.code = options.code;
    this.errorRedirect = options.errorRedirect ?? null;
  }
}

function readErrorRedirect(meta: unknown): string | null {
  if (!meta || typeof meta !== 'object') return null;
  const value = (meta as { errorRedirect?: unknown }).errorRedirect;
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function unwrap<T>(response: APIResponse<T>, fallback: string): T {
  if (response.error || response.data == null) {
    throw new Error(response.error?.message || fallback);
  }
  return response.data;
}

// ---------------------------------------------------------------------------
// Consent screen
// ---------------------------------------------------------------------------

export async function fetchOAuthConsentContext(
  params: OAuthAuthorizeRequestParams,
): Promise<OAuthConsentContext> {
  const response = await api.post<OAuthConsentContext>(
    '/api/oauth/authorize/context',
    params,
  );

  if (response.error || response.data == null) {
    throw new OAuthConsentError(
      response.error?.message || 'Could not load this sign-in request.',
      {
        code: response.error?.code ?? 'OAUTH_INVALID_REQUEST',
        errorRedirect: readErrorRedirect(response.meta),
      },
    );
  }

  return response.data;
}

export async function approveOAuthConsent(
  input: OAuthApproveInput,
): Promise<OAuthRedirectResult> {
  const response = await api.post<OAuthRedirectResult>(
    '/api/oauth/authorize/approve',
    input,
  );

  if (response.error || !response.data?.redirectUrl) {
    throw new OAuthConsentError(
      response.error?.message || 'Could not complete the sign-in request.',
      {
        code: response.error?.code ?? 'OAUTH_INVALID_REQUEST',
        errorRedirect: readErrorRedirect(response.meta),
      },
    );
  }

  return response.data;
}

export async function denyOAuthConsent(
  params: OAuthAuthorizeRequestParams,
): Promise<OAuthRedirectResult> {
  const response = await api.post<OAuthRedirectResult>(
    '/api/oauth/authorize/deny',
    params,
  );

  if (response.error || !response.data?.redirectUrl) {
    throw new OAuthConsentError(
      response.error?.message || 'Could not cancel the sign-in request.',
      {
        code: response.error?.code ?? 'OAUTH_INVALID_REQUEST',
        errorRedirect: readErrorRedirect(response.meta),
      },
    );
  }

  return response.data;
}

// ---------------------------------------------------------------------------
// Connected apps (the user's grants)
// ---------------------------------------------------------------------------

export async function fetchConnectedApps(): Promise<ConnectedAppSummary[]> {
  const response = await api.get<{ connections: ConnectedAppSummary[] }>(
    '/api/connected-apps',
  );
  return unwrap(response, 'Could not load connected apps.').connections ?? [];
}

export async function updateConnectedApp(
  connectionId: string,
  input: UpdateConnectedAppInput,
): Promise<ConnectedAppSummary> {
  const response = await api.patch<{ connection: ConnectedAppSummary }>(
    `/api/connected-apps/${encodeURIComponent(connectionId)}`,
    input,
  );
  return unwrap(response, 'Could not update this app.').connection;
}

export async function revokeConnectedApp(
  connectionId: string,
): Promise<{ revoked: boolean }> {
  const response = await api.delete<{ revoked: boolean }>(
    `/api/connected-apps/${encodeURIComponent(connectionId)}`,
  );
  return unwrap(response, 'Could not disconnect this app.');
}
